import { useState, useEffect, useCallback } from 'react';
import LoginForm from './components/LoginForm';
import MainPage from './components/MainPage';
import checkTimezone from './normalize';
import { API_URL } from './config/api';

const clearAllCookies = async () => {
  if (typeof window !== 'undefined' && 'cookieStore' in window) {
    try {
      const cookies = await window.cookieStore.getAll();
      for (const cookie of cookies) {
        await window.cookieStore.delete(cookie.name, { path: '/' });
      }
      await window.cookieStore.delete('_greenmag_key', { path: '/' });
    } catch (err) {}
  } else {
    document.cookie.split(';').forEach((c) => {
      const cookieName = c.split('=')[0].trim();
      document.cookie = `${cookieName}=; expires=${new Date(0).toUTCString()}; path=/;`;
    });
    document.cookie =
      '_greenmag_key=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token')); 
  const [company, setCompany] = useState(null);
  const [bootLoading, setBootLoading] = useState(true); 

  const fetchAccount = useCallback(async (accessToken) => {
    if (!accessToken || !API_URL) return false;

    const accountRes = await fetch(`${API_URL}/api/account`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!accountRes.ok) return false;

    const accountData = await accountRes.json();
    const account = accountData?.data;
    if (!account) return false;

    if (account.temp_password === true) return false;

    setUser({
      id: account.id,
      name: account.name,
      surname: account.surname,
      email: account.email,
      phone: account.phone,
      status: account.status,
    });

    setCompany(account.org);

    if (account?.org) {
      await checkTimezone(account.org);
    }

    return true;
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        const saved = localStorage.getItem('token');
        if (saved) {
          const ok = await fetchAccount(saved);
          if (!ok) {
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            setCompany(null);
          }
        }
      } finally {
        setBootLoading(false);
      }
    };

    run();
  }, [fetchAccount]);

  const handleLogin = async (login, password) => {
    localStorage.clear();
    sessionStorage.clear();
    await clearAllCookies();

    if (!API_URL) {
      throw new Error('REACT_APP_API_URL не задан. Проверь .env и перезапусти проект.');
    }

    const res = await fetch(`${API_URL}/api/auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login, password }),
    });

    if (!res.ok) throw new Error('Ошибка авторизации');

    const data = await res.json();
    const accessToken = data?.data?.access_token;
    if (!accessToken) throw new Error('Токен не получен');

    setToken(accessToken);
    localStorage.setItem('token', accessToken);

    const accountRes = await fetch(`${API_URL}/api/account`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!accountRes.ok) throw new Error('Ошибка аккаунта');

    const accountData = await accountRes.json();
    const account = accountData.data;

    if (account.temp_password === true) {
      return {
        temp_password: true,
        token: accessToken,
      };
    }

    setUser({
      id: account.id,
      name: account.name,
      surname: account.surname,
      email: account.email,
      phone: account.phone,
      status: account.status,
    });

    setCompany(account.org);

    if (account?.org) {
      await checkTimezone(account.org);
    }

    return { temp_password: false };
  };

  const handleLogout = async () => {
    try {
      if (token && API_URL) {
        await fetch(`${API_URL}/api/account/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUser(null);
      setToken(null);
      setCompany(null);
      localStorage.clear();
      sessionStorage.clear();
      await clearAllCookies();
    }
  };

  if (bootLoading) return null;

  if (user) {
    return (
      <MainPage
        user={user}
        company={company}
        token={token}
        onLogout={handleLogout}
      />
    );
  }

  return <LoginForm onLogin={handleLogin} />;
}

export default App;
